import { useState } from "react";
import { FileSpreadsheet, Loader2, ScanText } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";

// Lee la ficha de inscripción (.xls/.xlsx) o una captura y devuelve alumnos,
// contacto y observaciones. Para guardarlos en un curso: saveAttendanceData.
const parseExcel = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await adminApi.post("/admin/courses/attendance-excel", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

const readDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const parseScreenshot = async (file) => {
  if (!file.type.startsWith("image/")) {
    throw new Error("El archivo debe ser una imagen (JPG/PNG/WEBP).");
  }
  if (file.size > 8 * 1024 * 1024) {
    throw new Error("La imagen es demasiado grande (máximo 8 MB).");
  }
  const res = await adminApi.post("/admin/courses/attendance-screenshot", {
    image_base64: await readDataUrl(file),
  });
  return res.data;
};

export const saveAttendanceData = async (courseId, data) => {
  const res = await adminApi.post(`/admin/courses/${courseId}/attendance-data`, {
    students: data.students || [],
    contact_name: data.contact_name || "",
    contact_phone: data.contact_phone || "",
    contact_email: data.contact_email || "",
    observations: data.observations || "",
  });
  return res.data;
};

const LoaderButton = ({ kind, onLoaded }) => {
  const [loading, setLoading] = useState(false);
  const excel = kind === "excel";
  const Icon = excel ? FileSpreadsheet : ScanText;

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true);
    try {
      onLoaded(await (excel ? parseExcel(file) : parseScreenshot(file)), file.name);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message
          || (excel ? "No se pudo leer el Excel." : "No se pudo leer la captura.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <label
      className={`inline-flex items-center gap-2 h-10 px-4 rounded-md border-2 border-dashed font-semibold cursor-pointer transition-colors text-sm ${
        loading
          ? "border-zinc-300 bg-zinc-100 text-zinc-500 cursor-wait"
          : "border-emerald-700 text-emerald-800 hover:bg-emerald-50"
      }`}
      data-testid={`course-attendance-${kind}-label`}
      title={
        excel
          ? "Sube la ficha de inscripción (.xls o .xlsx): carga alumnos, contacto y observaciones"
          : "Sube una captura del listado de alumnos: carga apellidos, nombre y DNI"
      }
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
      {loading
        ? excel ? "Leyendo Excel…" : "Leyendo captura…"
        : excel ? "CARGA asistencias Excel" : "CARGA asistencias por captura"}
      <input
        type="file"
        accept={
          excel
            ? ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "image/png,image/jpeg,image/webp"
        }
        className="hidden"
        onChange={onChange}
        disabled={loading}
        data-testid={`course-attendance-${kind}-input`}
      />
    </label>
  );
};

export const AttendanceLoaders = ({ onLoaded }) => (
  <>
    <LoaderButton kind="excel" onLoaded={onLoaded} />
    <LoaderButton kind="screenshot" onLoaded={onLoaded} />
  </>
);
